import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/** One response shape for all quick-stat cards (landing + client-stats + charts). */
export interface QuickStatsData {
	totalStudents: number;
	totalAssessments: number;
	notAssessed: number;
	phase1: number;
	phase2: number;
	phase3: number;
	complete: number;
}

/** Response shape from ReadingAchievementController.getAllPhasesData. */
interface AllPhasesData {
	notAssessedCounts?: { current?: { students?: Record<string, any> } };
	completed?: { current?: { count?: number; students?: Record<string, any> } };
	phases?: {
		1011?: { current?: { divisions?: { data?: number[] }; students?: Record<string, any> } };
		1012?: { current?: { divisions?: { data?: number[] }; students?: Record<string, any> } };
		1013?: { current?: { divisions?: { data?: number[] }; students?: Record<string, any> } };
	};
}

function studentCount(students: Record<string, any> | any[] | undefined): number {
	if (!students) return 0;
	return Array.isArray(students) ? students.length : Object.keys(students).length;
}

function studentsAsArray(students: Record<string, any> | any[] | undefined): any[] {
	if (!students) return [];
	return Array.isArray(students) ? students : Object.values(students);
}

@Injectable({
	providedIn: 'root',
})
export class QuickStatsService {
	/** One shared in-flight/completed request per node so dashboard widgets do not duplicate AllPhases HTTP calls. */
	private readonly allPhasesSharedByNodeId = new Map<number, Observable<AllPhasesData | null>>();

	constructor(private http: HttpClient) {}

	/** Legacy landing page API — keep for existing Quick Stats cards. */
	public totalStudents$(nodeId: number): Observable<any> {
		return this.http.get<any>(`${environment.apiUrl}/report/totalStudents/nodeId/${nodeId}`);
	}

	public totalAssessments$(nodeId: number): Observable<any> {
		return this.http.get<any>(`${environment.apiUrl}/report/assessments/nodeId/${nodeId}`);
	}

	public phases$(nodeId: number): Observable<any> {
		return this.http.get<any>(`${environment.apiUrl}/report/phaseTotals/nodeId/${nodeId}`);
	}

	/** Fetches reading achievement data (getAllPhasesData). Used by PARA dashboard charts and client-stats. */
	public allPhasesData$(nodeId: number): Observable<AllPhasesData | null> {
		let shared = this.allPhasesSharedByNodeId.get(nodeId);
		if (!shared) {
			shared = this.http
				.get<AllPhasesData>(`${environment.apiUrl}/readingAchievement/AllPhases/nodeId/${nodeId}`)
				.pipe(
					catchError(() => of(null)),
					shareReplay({ bufferSize: 1, refCount: false })
				);
			this.allPhasesSharedByNodeId.set(nodeId, shared);
		}
		return shared;
	}

	public quickStatsForNode$(nodeId: number): Observable<QuickStatsData> {
		return this.allPhasesData$(nodeId).pipe(map((data) => this.mapAllPhasesToQuickStats(data)));
	}

	private mapAllPhasesToQuickStats(data: AllPhasesData | null): QuickStatsData {
		if (!data) {
			return {
				totalStudents: 0,
				totalAssessments: 0,
				notAssessed: 0,
				phase1: 0,
				phase2: 0,
				phase3: 0,
				complete: 0,
			};
		}

		const mode = 'current';
		const phaseSum = (phaseId: 1011 | 1012 | 1013): number => {
			const div = data.phases?.[phaseId]?.[mode]?.divisions?.data;
			return Array.isArray(div) ? div.reduce((a, b) => a + b, 0) : 0;
		};

		const phase1 = phaseSum(1011);
		const phase2 = phaseSum(1012);
		const phase3 = phaseSum(1013);
		const notAssessed = studentCount(data.notAssessedCounts?.[mode]?.students);
		const complete =
			data.completed?.[mode]?.count ?? studentCount(data.completed?.[mode]?.students);

		const totalStudents = phase1 + phase2 + phase3 + notAssessed + complete;

		const allStudents = [
			...studentsAsArray(data.phases?.[1011]?.[mode]?.students),
			...studentsAsArray(data.phases?.[1012]?.[mode]?.students),
			...studentsAsArray(data.phases?.[1013]?.[mode]?.students),
			...studentsAsArray(data.completed?.[mode]?.students),
			...studentsAsArray(data.notAssessedCounts?.[mode]?.students),
		];
		const totalAssessments = allStudents.reduce(
			(sum, s) => sum + (typeof s?.timesAssessed === 'number' ? s.timesAssessed : 0),
			0
		);

		return {
			totalStudents,
			totalAssessments,
			notAssessed,
			phase1,
			phase2,
			phase3,
			complete,
		};
	}
}
